import React, { useEffect, useCallback, useMemo, memo, useRef, useState } from "react";
import { useBlocker, useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import logo_icon from "../assets/img/logo_icon.png";
import DOMPurify from 'dompurify';
import { FaTimes } from "react-icons/fa";
import { IoIosArrowDown, IoIosArrowUp } from "react-icons/io";
import '../assets/css/mainLayout.css';
import ProjectView from "../components/agents/ProjectView";
import { NotificationProvider } from "../components/common/NotificationProvider";
import { showNotificationGlobal } from "../components/common/NotificationProvider";
import SAModalConfirm from "../components/common/SAModalConfirm";
import AgentService from "../services/api/agents";
import BillingService from "../services/api/billing";
import { ACCESS_TOKEN, API_RESPONSE, default_size_messages } from "../settings/shared/constants";
import { TrialCreditAlert } from "../components/common/TrialUsageSummary";
import { TRIAL_USAGE_UPDATED_EVENT } from "../components/agents/useChatMessages";

const sanitizeText = (text) => {
  if (!text || typeof text !== 'string') return 'Untitled Project';
  return DOMPurify.sanitize(text, { ALLOWED_TAGS: [], ALLOWED_ATTR: [] });
};

const ALL_PROJECT_SECTIONS = [
  { key: 'frontend', label: 'Frontend', icon: 'fa-desktop', color: '#3b82f6' },
  { key: 'backend', label: 'Backend', icon: 'fa-server', color: '#10b981' },
  { key: 'agents', label: 'Agents', icon: 'fa-robot', color: '#f59e0b' },
];

function isWebsiteProjectDto(dto) {
  const raw = dto?.appType;
  if (raw == null || raw === '') return false;
  return String(raw).toUpperCase() === 'WEBSITE';
}

function isXrayProjectDto(dto) {
  const raw = dto?.appType;
  if (raw == null || raw === '') return false;
  return String(raw).toUpperCase() === 'XRAY';
}

function clampSectionForWebsite(section) {
  return section === 'backend' || section === 'agents' ? 'frontend' : section;
}

function getProjectMessagesCount(projectData) {
  const list = projectData?.messages;
  return Array.isArray(list) ? list.length : 0;
}

/** Website and Xray open Frontend. No messages → Agents; has messages → Frontend. */
function resolveActiveSectionForProject({ projectData, prevSection, sectionFromUrl }) {
  if (isWebsiteProjectDto(projectData)) {
    return clampSectionForWebsite(prevSection || sectionFromUrl || 'frontend');
  }
  if (isXrayProjectDto(projectData)) {
    return 'frontend';
  }
  if (getProjectMessagesCount(projectData) === 0) {
    return 'agents';
  }
  return 'frontend';
}

// Memoized Project Popup Component
const ProjectPopup = memo(({ 
  projectName, 
  onClose, 
  onNavigate, 
  showAppearanceSubmenu,
  showThemeSubmenu,
  onToggleAppearance,
  onToggleTheme 
}) => (
  <div className="project-popup">
    <div className="project-popup-header">
      <div className="project-popup-title">Project Details</div>
    </div>
    <div className="project-popup-content">
      <div className="project-detail-item">
        <div className="project-detail-label">Project Name</div>
        <div className="project-detail-value">{projectName}</div>
      </div>
      <div className="project-detail-item">
        <div className="project-detail-label">Last Modified</div>
        <div className="project-detail-value">{new Date().toLocaleDateString()}</div>
      </div>

      
      <div className="project-popup-footer">
        <button className="dashboard-btn" onClick={onNavigate}>
          <div className="shimmer-effect"></div>
          <i className="fa-solid fa-arrow-left"></i>
          <span>Back to Projects</span>
          <i className="fa-solid fa-external-link-alt external-icon"></i>
        </button>
      </div>
    </div>
  </div>
));

ProjectPopup.displayName = 'ProjectPopup';

// Memoized Section Navigation Component
const SectionNavigation = memo(({ activeSection, onSectionChange, sections }) => {
  const items = sections ?? ALL_PROJECT_SECTIONS;
  const activeIndex = Math.max(0, items.findIndex((s) => s.key === activeSection));

  return (
    <div className="project-sections-container font-inter-semi-bold">
      <div className="project-sections-nav">
        <div
          className="segmented-control-wrapper"
          style={{ "--sa-section-count": items.length }}
        >
          <div 
            className="segment-background-slider"
            style={{
              transform: `translateX(${activeIndex * 100}%)`
            }}
          />
          
          <div className="sections-tabs-wrapper">
            {items.map((section, index) => (
              <div
                key={section.key}
                className={`section-tab-item ${activeSection === section.key ? 'active' : ''}`}
                onClick={() => onSectionChange(section.key)}
                style={{
                  '--section-color': section.color,
                  '--segment-index': index
                }}
              >
                <div className="section-tab-content">
                  <div className="section-icon-wrapper">
                    <i className={`fas ${section.icon}`}></i>
                    {activeSection === section.key && (
                      <div className="icon-glow-effect"></div>
                    )}
                  </div>
                  <div className="section-info">
                    <div className="section-label">{section.label}</div>
                  </div>
                  {activeSection === section.key && (
                    <div className="active-indicator">
                      <div className="active-dot"></div>
                      <div className="active-pulse"></div>
                    </div>
                  )}
                </div>
                <div className="segment-ripple-effect"></div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
});

SectionNavigation.displayName = 'SectionNavigation';

const LayoutProjectView = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id: projectId } = useParams();
  const [searchParams] = useSearchParams();
  const sectionFromUrl = searchParams.get('section');
  const [state, setState] = useState({
    showProjectPopup: false,
    projectName: 'Untitled Project',
    projectData: {},
    activeSection: sectionFromUrl || 'agents',
    showAppearanceSubmenu: false,
    showThemeSubmenu: false,
    hasUnsavedWorkflowChanges: false,
  }); 
  const [showUnsavedConfirm, setShowUnsavedConfirm] = useState(false);
  const [trialUsage, setTrialUsage] = useState(null);
  const [creditAlertDismissed, setCreditAlertDismissed] = useState(false);
  const pendingActionRef = useRef(null);
  const unsavedmodalstatusRef = useRef(false);
  const prevMessagesCountRef = useRef(null);

  const projectAppTypeRaw = state.projectData?.appType;
  const isWebsiteProject = useMemo(() => {
    if (projectAppTypeRaw == null || projectAppTypeRaw === '') return false;
    return String(projectAppTypeRaw).toUpperCase() === 'WEBSITE';
  }, [projectAppTypeRaw]);

  const sectionNavItems = useMemo(() => {
    if (isWebsiteProject) {
      return ALL_PROJECT_SECTIONS.filter((s) => s.key === 'frontend');
    }
    return ALL_PROJECT_SECTIONS;
  }, [isWebsiteProject]);

  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) => {
      if (unsavedmodalstatusRef.current) return false;
      if (state.activeSection !== 'agents') return false;
      if (!state.hasUnsavedWorkflowChanges) return false;
      return (
        currentLocation.pathname !== nextLocation.pathname ||
        currentLocation.search !== nextLocation.search ||
        currentLocation.hash !== nextLocation.hash
      );
    }
  );

  useEffect(() => {
    if (blocker.state !== "blocked") return;
    if (state.activeSection !== 'agents') return;
    if (!showUnsavedConfirm) {
      pendingActionRef.current = () => blocker.proceed();
      setShowUnsavedConfirm(true);
    }
  }, [blocker, showUnsavedConfirm, state.activeSection]);

  // Security: Check authentication on mount and navigation
  useEffect(() => {
    const token = localStorage.getItem(ACCESS_TOKEN);
    if (!token) {
      navigate('/auth/login', { replace: true });
    }
  }, [navigate]);

  // Shared project fetch + state hydration, used by:
  // 1) initial mount
  // 2) workflow restore (refresh the template/canvas)
  const fetchProject = useCallback(async () => {
    if (!projectId) return;
    try {
      const postData = { id: projectId };
      const xrayJobType = String(
        location.state?.findingChatDraft?.xrayJobType || ""
      ).toUpperCase();

      if (["XRAY_MODERNIZE", "XRAY_FIX"].includes(xrayJobType)) {
        postData.xrayJobType = xrayJobType;
      }
      /** Page size for project chat messages (same as ChatComponent pagination). */
      const response = await AgentService.getProject(0, postData, default_size_messages);

      if (response?.status === API_RESPONSE.SUCCESS && response?.projectDTO) {
        // Security: Sanitize project name before setting state
        const sanitizedName = sanitizeText(response.projectDTO.name);

        // Pass the full response structure to ProjectView so DrawWorkFlow can access workFlowTemplateDTO
        // Create a structure that includes both projectDTO properties and the full response structure
        const msgList = response.projectDTO?.messages;
        const msgCount = Array.isArray(msgList) ? msgList.length : 0;
        const fullProjectData = {
          // Spread projectDTO properties for backward compatibility
          ...response.projectDTO,
          // Include workFlowTemplateDTO at top level so DrawWorkFlow can find it
          workFlowTemplateDTO: response.workFlowTemplateDTO || null,
          // Include the full response structure for DrawWorkFlow's showWorkflowData function
          status: response.status,
          projectDTO: response.projectDTO,
          // Chat history pagination (getProject ?page=&size= applies to messages)
          messagesPageSize: default_size_messages,
          messagesHasMore: msgCount >= default_size_messages,
        };
        prevMessagesCountRef.current = msgCount;
        setState(prevState => ({
          ...prevState,
          projectName: sanitizedName || fullProjectData?.name,
          projectData: fullProjectData,
          activeSection: resolveActiveSectionForProject({
            projectData: fullProjectData,
            prevSection: prevState.activeSection,
            sectionFromUrl,
          }),
        }));
      }
    } catch (error) {
      // ignore
    }
  }, [projectId, default_size_messages, sectionFromUrl, location.state]);

  useEffect(() => {
    fetchProject();
  }, [fetchProject]);

  useEffect(() => {
    const accountId = localStorage.getItem("accountId");
    if (!accountId) return undefined;

    let cancelled = false;
    BillingService.getTrialUsageSummary()
      .then((response) => {
        if (cancelled) return;
        const trial = response?.trialUsageSummary;
        if (trial) {
          setTrialUsage(trial);
          setCreditAlertDismissed(false);
        }
      })
      .catch(() => {});

    return () => {
      cancelled = true;
    };
  }, [projectId]);

  useEffect(() => {
    const onTrialUpdated = (event) => {
      const trial = event?.detail;
      if (!trial) return;
      setTrialUsage(trial);
      setCreditAlertDismissed(false);
    };
    window.addEventListener(TRIAL_USAGE_UPDATED_EVENT, onTrialUpdated);
    return () => window.removeEventListener(TRIAL_USAGE_UPDATED_EVENT, onTrialUpdated);
  }, []);

  useEffect(() => {
    prevMessagesCountRef.current = null;
  }, [projectId]);

  const syncActiveSectionForMessages = useCallback(() => {
    setState((prev) => {
      if (!prev.projectData?.id) return prev;
      if (isWebsiteProjectDto(prev.projectData) || isXrayProjectDto(prev.projectData)) return prev;

      const msgCount = getProjectMessagesCount(prev.projectData);
      const prevCount = prevMessagesCountRef.current;
      prevMessagesCountRef.current = msgCount;

      if (prevCount === msgCount && prevCount !== null) {
        return prev;
      }

      let next = prev.activeSection;
      if (msgCount === 0) {
        next = 'agents';
      } else if (prevCount === 0 || prevCount === null) {
        next = 'frontend';
      } else {
        return prev;
      }

      return next === prev.activeSection ? prev : { ...prev, activeSection: next };
    });
  }, []);

  useEffect(() => {
    if (!state.projectData?.id) return;
    syncActiveSectionForMessages();
  }, [state.projectData?.id, state.projectData?.messages, syncActiveSectionForMessages]);

  // When a workflow "restore default template" happens inside the workflow UI,
  // refresh project data so the canvas/template updates immediately.
  useEffect(() => {
    const onWorkflowRestoredDefault = () => fetchProject();
    window.addEventListener('workflow-restored-default', onWorkflowRestoredDefault);
    return () => {
      window.removeEventListener('workflow-restored-default', onWorkflowRestoredDefault);
    };
  }, [fetchProject]);

  // Refresh project/workflow data after a successful save so UI reflects immediately.
  useEffect(() => {
    const handler = () => fetchProject();
    window.addEventListener('workflow-saved', handler);
    return () => window.removeEventListener('workflow-saved', handler);
  }, [fetchProject]);

  // Update active section when URL parameter changes (after project is loaded)
  useEffect(() => {
    if (!sectionFromUrl || !['frontend', 'backend', 'agents'].includes(sectionFromUrl)) return;
    if (!state.projectData?.id) return;
    const nextSection = resolveActiveSectionForProject({
      projectData: state.projectData,
      prevSection: sectionFromUrl,
      sectionFromUrl,
    });
    setState((prevState) =>
      prevState.activeSection === nextSection
        ? prevState
        : { ...prevState, activeSection: nextSection },
    );
  }, [
    sectionFromUrl,
    state.projectData?.id,
    state.projectData?.messages,
    state.projectData?.appType,
  ]);

  // Listen for workflow dirty state (from DrawWorkFlow) to warn on navigation/section changes
  useEffect(() => {
    const handler = (e) => {
      const isChanged = Boolean(e?.detail?.isChanged);
      setState(prev => ({ ...prev, hasUnsavedWorkflowChanges: isChanged }));
    };
    window.addEventListener('workflow-simple-changed', handler);
    return () => window.removeEventListener('workflow-simple-changed', handler);
  }, []);

  // Warn on browser refresh/close while workflow is dirty
  useEffect(() => {
    if (!state.hasUnsavedWorkflowChanges) return;
    const handler = (e) => {
      e.preventDefault();
      e.returnValue = "";
      return "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [state.hasUnsavedWorkflowChanges]);

  const requestUnsavedConfirm = useCallback((action) => {
    if (showUnsavedConfirm) return;
    // Only show this modal while the workflow editor (Agents tab) is active.
    if (state.activeSection !== 'agents') return;
    if (unsavedmodalstatusRef.current) {
      unsavedmodalstatusRef.current = false;
      if (typeof action === 'function') action();
      return;
    }
    pendingActionRef.current = action;
    setShowUnsavedConfirm(true);
  }, [showUnsavedConfirm, state.activeSection]);

  const runPendingAction = useCallback(() => {
    const action = pendingActionRef.current;
    pendingActionRef.current = null;
    if (typeof action === 'function') action();
  }, []);

  const handleUnsavedSkip = useCallback(() => {
    setShowUnsavedConfirm(false);
    unsavedmodalstatusRef.current = true;
    window.dispatchEvent(new CustomEvent('workflow-discard-changes'));
    runPendingAction();
    setTimeout(() => {
      unsavedmodalstatusRef.current = false;
    }, 0);
  }, [runPendingAction]);

  const handleUnsavedSave = useCallback(() => {
    setShowUnsavedConfirm(false);

    let didResolve = false;
    const cleanupFns = [];
    const resolveSaved = () => {
      if (didResolve) return;
      didResolve = true;
      unsavedmodalstatusRef.current = true;
      cleanupFns.forEach((fn) => {
        try { fn(); } catch (_) {}
      });
      runPendingAction();
      setTimeout(() => {
        unsavedmodalstatusRef.current = false;
      }, 0);
    };

    // Save completes either via explicit 'workflow-saved' 
    const onSaved = () => resolveSaved();
    const onWorkflowChanged = (e) => {
      const isChanged = Boolean(e?.detail?.isChanged);
      if (!isChanged) resolveSaved();
    };

    window.addEventListener('workflow-saved', onSaved);
    cleanupFns.push(() => window.removeEventListener('workflow-saved', onSaved));
    window.addEventListener('workflow-simple-changed', onWorkflowChanged);
    cleanupFns.push(() => window.removeEventListener('workflow-simple-changed', onWorkflowChanged));

    window.dispatchEvent(new CustomEvent('save-workflow'));

    // Failsafe: if save never completes, keep user in place and notify.
    setTimeout(() => {
      if (didResolve) return;
      cleanupFns.forEach((fn) => {
        try { fn(); } catch (_) {}
      });
      showNotificationGlobal(
        "Save didn't complete. Please try again, or choose Skip to leave without saving.",
        'error',
        'bottomCenter'
      );
    }, 12000);
  }, [runPendingAction]);

  // Memoized callbacks to prevent unnecessary re-renders
  const toggleProjectPopup = useCallback(() => {
    setState(prevState => ({
      ...prevState,
      showProjectPopup: !prevState.showProjectPopup,
    }));
  }, []);

  const popupRef = useRef(null);

  useEffect(() => {
    if (!state.showProjectPopup) return;

    const handleClick = (event) => {
      if (
        popupRef.current &&
        !popupRef.current.contains(event.target)
      ) {
        setState(prevState => ({
          ...prevState,
          showProjectPopup: false
        }));
      }
    };

    const timer = setTimeout(() => {
      document.addEventListener("click", handleClick);
    }, 100);

    return () => {
      clearTimeout(timer);
      document.removeEventListener("click", handleClick);
    };
  }, [state.showProjectPopup]);

  

  const handleSectionChange = useCallback((section) => {
    const next =
      isWebsiteProject ? clampSectionForWebsite(section) : section;
    if (state.hasUnsavedWorkflowChanges) {
      requestUnsavedConfirm(() => {
        // Update active section to stay on project page without changing URL
        setState(prevState => ({
          ...prevState,
          activeSection: next
        }));
      });
      return;
    }
    // Update active section to stay on project page without changing URL
    setState(prevState => ({
      ...prevState,
      activeSection: next
    }));
  }, [requestUnsavedConfirm, state.hasUnsavedWorkflowChanges, isWebsiteProject]);

  const handleNavigateToApps = useCallback(() => {
    if (state.hasUnsavedWorkflowChanges) {
      requestUnsavedConfirm(() => {
        navigate('/apps');
        setState(prevState => ({ ...prevState, showProjectPopup: false }));
      });
      return;
    }
    navigate('/apps');
    setState(prevState => ({ ...prevState, showProjectPopup: false }));
  }, [navigate, requestUnsavedConfirm, state.hasUnsavedWorkflowChanges]);

  const handleCloseProject = useCallback(() => {
    if (state.hasUnsavedWorkflowChanges) {
      requestUnsavedConfirm(() => {
        navigate('/apps');
      });
      return;
    }
    navigate('/apps');
  }, [navigate, requestUnsavedConfirm, state.hasUnsavedWorkflowChanges]);

  const toggleAppearanceSubmenu = useCallback(() => {
    setState(prev => ({ ...prev, showAppearanceSubmenu: !prev.showAppearanceSubmenu }));
  }, []);

  const toggleThemeSubmenu = useCallback((e) => {
    e?.stopPropagation();
    setState(prev => ({ ...prev, showThemeSubmenu: !prev.showThemeSubmenu }));
  }, []);

  return (
    <NotificationProvider>
        <div className="project-layout">
      <SAModalConfirm
        show={showUnsavedConfirm && state.activeSection === 'agents'}
        centered={true}
        title="Unsaved changes"
        message="This workflow has unsaved changes. Do you want to save before leaving?"
        okText="Save"
        cancelText="Skip"
        handleOk={handleUnsavedSave}
        handleCancel={handleUnsavedSkip}
        zIndex={2200}
      />
      {/* Header Section */}
      <div className="project-header">
        <div className="header-left">
          <div className="project-controls-section">
            <div className="project-name-section">
              <div className="project-name-card" onClick={toggleProjectPopup}>
                  <div style={{ position: 'relative' }}>
                  <span
                    style={{
                      position: 'absolute',
                      top: -2,
                      right: -1,
                      fontSize: 6,
                      fontWeight: 'bold',
                      background: 'linear-gradient(to right, #f25c6a, #a13b45, #ff707e) text',
                      WebkitBackgroundClip: 'text',
                      color: 'transparent'
                    }}
                  >
                    TM
                  </span>
                    <img src={logo_icon} alt="logo" className="project-logo" />
                  </div>
                <div className="project-info">
                  <span className="project-title text-ellipsis">{state?.projectName || 'Untitled Project'}</span>
                  <span className="project-subtitle text-ellipsis">AI-powered project workspace</span>
                </div>
                <button className="project-toggle-btn" type="button" aria-label="Toggle project menu">
                  {state.showProjectPopup ? <IoIosArrowUp size={18} /> : <IoIosArrowDown size={18} />}
                </button>
              </div>

              {/* Project Popup */}
                {state.showProjectPopup && (
                  <div ref={popupRef}>
                    <ProjectPopup
                      projectName={state.projectName}
                      onClose={toggleProjectPopup}
                      onNavigate={handleNavigateToApps}
                      showAppearanceSubmenu={state.showAppearanceSubmenu}
                      showThemeSubmenu={state.showThemeSubmenu}
                      onToggleAppearance={toggleAppearanceSubmenu}
                      onToggleTheme={toggleThemeSubmenu}
                    />
                  </div>
                )}
            </div>

            {/* Project Sections Navigation */}
            <SectionNavigation
              activeSection={state.activeSection}
              onSectionChange={handleSectionChange}
              sections={sectionNavItems}
            />

            {/* Right Side Icons */}
            <div className="header-right-icons">
                <button
                  onClick={handleCloseProject}
                  className="close-project-btn"
                  type="button"
                  title="Back to Projects"
                  aria-label="Close project"
                >
                  <i className="fa-solid fa-xmark" style={{ fontSize: 15 }} />
                </button>
            </div>
          </div>
        </div>
      </div>

      {!creditAlertDismissed ? (
        <TrialCreditAlert
          trial={trialUsage}
          onUpgrade={() => navigate("/settings?view=credit-usage")}
          onDismiss={() => setCreditAlertDismissed(true)}
        />
      ) : null}

      {/* Main Content */}
      {state.projectData?.id && (
        <ProjectView
          projectData={state.projectData}
          activeSection={state.activeSection}
          onRequireAgentsSection={syncActiveSectionForMessages}
        />
      )}

      {!state.projectData?.id && (
        <div className="loading-container">
          <div className="spinner-container">
            <div className="spinner-ring"></div>
            <div className="spinner-ring-inner"></div>
          </div>
          <div className="loading-text-container">
            <span className="loading-text">Loading Project</span>
          </div>
        </div>
      )}

        </div>
    </NotificationProvider>
  );
};

export default LayoutProjectView;
